import { configurationSettings } from '../../../../configuration/configuration-settings';
import { isSeatFirstOrderingForShowtime } from '../../../browsing/selectors/showtimes/is-seat-first-ordering-for-showtime';
import { getMaximumAllowedMemberRewards } from '../../../loyalty/utils/member-reward-utils';
import { getTicketTypeById } from '../../../sales-data/selectors/tickets/get-ticket-type-by-id';
import { getPackageTicketTypeTicketCount, isPackageTicketType } from '../../../sales-data/utils/tickets/ticket-type-utils';
import { TicketQuantityStatus } from '../../types/ticket-quantity-status';
import { getOrderSeatCountForAreaCategory } from '../seats/get-order-seat-count-for-area-category';
import { getOrderTicketRedemptionCardRedemptionDetails } from '../ticket-redemption-cards/get-order-ticket-redemption-card-redemption-details';
import { getOrderVoucherRedemptionDetails } from '../vouchers/get-order-voucher-redemption-details';
import { getOrderTicketCount } from './get-order-ticket-count';
import { getOrderTicketCountForAreaCategory } from './get-order-ticket-count-for-area-category';
import { getOrderTicketQuantityForRedemptionCard } from './get-order-ticket-quantity-for-redemption-card';
import { getOrderTicketQuantityForReward } from './get-order-ticket-quantity-for-reward';
import { getOrderTicketQuantityForShowtime } from './get-order-ticket-quantity-for-showtime';
import { getOrderTicketQuantityForVoucher } from './get-order-ticket-quantity-for-voucher';
export const getAllowedOrderTicketQuantity = (showtimeId, ticket) => {
    const isSeatFirstOrdering = isSeatFirstOrderingForShowtime(showtimeId);
    const ticketType = getTicketTypeById(ticket.ticketTypeId);
    if (!ticketType)
        throw new Error(`Cannot find ticket type with ID ${ticket.ticketTypeId}.`);
    const currentQuantity = getOrderTicketQuantityForShowtime(showtimeId, ticket);
    return isSeatFirstOrdering
        ? getAllowedSeatFirstOrderTicketQuantity(showtimeId, ticket, ticketType, currentQuantity)
        : getAllowedTicketFirstOrderTicketQuantity(ticket, ticketType, currentQuantity);
};
/**
 * In ticket-first ordering mode, the maximum quantity of a ticket is determined by the maximum number of tickets allowed in an order.
 */
const getAllowedTicketFirstOrderTicketQuantity = (ticket, ticketType, currentQuantity) => {
    const { maximumTicketsPerOrder } = configurationSettings.data.order;
    const selectedTicketCount = getOrderTicketCount();
    const remainingQuantity = maximumTicketsPerOrder - selectedTicketCount;
    return {
        status: TicketQuantityStatus.Allowed,
        max: getMaximumQuantityOfTicket(ticket, ticketType, currentQuantity, remainingQuantity)
    };
};
/**
 * In seat-first ordering mode, the maximum quantity of a ticket is determined by the seats selected.
 * The ticket is 'not allowed' if is is not valid for the selected seats.
 * The ticket is 'invalid' if the selected ticket quantity exceeds the number of selected seats.
 */
const getAllowedSeatFirstOrderTicketQuantity = (showtimeId, ticket, ticketType, currentQuantity) => {
    const { areaCategoryId } = ticketType;
    const selectedTicketCount = getOrderTicketCountForAreaCategory(showtimeId, areaCategoryId);
    const selectedSeatCount = getOrderSeatCountForAreaCategory(showtimeId, areaCategoryId);
    // Ticket has not been selected and no seats for this ticket have been selected
    if (currentQuantity === 0 && selectedSeatCount === 0) {
        return { status: TicketQuantityStatus.NotAllowed, max: 0 };
    }
    // Ticket has been selected and too many tickets have been selected for this area category
    if (currentQuantity > 0 && selectedTicketCount > selectedSeatCount) {
        return { status: TicketQuantityStatus.Invalid, max: currentQuantity };
    }
    const remainingQuantity = selectedSeatCount - selectedTicketCount;
    return {
        status: TicketQuantityStatus.Allowed,
        max: getMaximumQuantityOfTicket(ticket, ticketType, currentQuantity, remainingQuantity)
    };
};
const getMaximumQuantityOfTicket = (ticket, ticketType, currentQuantity, remainingQuantity) => {
    let adjustedRemainingQuantity = remainingQuantity;
    const { rewardId, voucherId, ticketRedemptionCardId } = ticket;
    if (isPackageTicketType(ticketType)) {
        adjustedRemainingQuantity = getRemainingQuantityForPackageTicketType(adjustedRemainingQuantity, ticketType);
    }
    if (!!rewardId) {
        adjustedRemainingQuantity = getRemainingQuantityForMemberReward(adjustedRemainingQuantity, rewardId);
    }
    if (!!voucherId) {
        adjustedRemainingQuantity = getRemainingQuantityForVoucher(adjustedRemainingQuantity, voucherId);
    }
    if (!!ticketRedemptionCardId) {
        adjustedRemainingQuantity = getRemainingQuantityForTicketRedemptionCard(adjustedRemainingQuantity, ticketRedemptionCardId);
    }
    return currentQuantity + adjustedRemainingQuantity;
};
const getRemainingQuantityForPackageTicketType = (remainingQuantity, ticketType) => {
    const ticketCount = getPackageTicketTypeTicketCount(ticketType);
    return Math.floor(remainingQuantity / ticketCount);
};
const getRemainingQuantityForMemberReward = (remainingQuantity, rewardId) => {
    const availableRedemptions = getMaximumAllowedMemberRewards([rewardId]);
    const currentTicketCount = getOrderTicketQuantityForReward(rewardId);
    const remainingRedemptions = availableRedemptions - currentTicketCount;
    return Math.min(remainingQuantity, remainingRedemptions);
};
const getRemainingQuantityForVoucher = (remainingQuantity, voucherId) => {
    const { availableRedemptions } = getOrderVoucherRedemptionDetails(voucherId);
    const currentTicketCount = getOrderTicketQuantityForVoucher(voucherId);
    const remainingRedemptions = availableRedemptions - currentTicketCount;
    return Math.min(remainingQuantity, remainingRedemptions);
};
const getRemainingQuantityForTicketRedemptionCard = (remainingQuantity, ticketRedemptionCardId) => {
    const { redemptionLimit } = getOrderTicketRedemptionCardRedemptionDetails(ticketRedemptionCardId);
    if (redemptionLimit.isUnlimited)
        return remainingQuantity;
    const currentTicketCount = getOrderTicketQuantityForRedemptionCard(ticketRedemptionCardId);
    const remainingRedemptions = redemptionLimit.maximum - currentTicketCount;
    return Math.min(remainingQuantity, remainingRedemptions);
};
